import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { fetchPrivateEvents } from '../../api/privateEvents';

/**
 * The private-events list for one region (or all of them).
 *
 * `events` is null until the current region's list arrives, so a region switch
 * shows "Loading…" rather than the last region's events under the new name.
 *
 * `visibleRegionIds` outlives a region switch or a failed load: it describes the
 * caller, not the filter. It is undefined only until the first list arrives, and
 * the picker reads that as "nothing to offer yet" — never as "every region".
 */
export function usePrivateEventsList(regionSlug: string | undefined) {
  const query = useQuery({
    queryKey: ['private-events', regionSlug ?? 'all'],
    queryFn: () => fetchPrivateEvents(regionSlug),
    retry: false,
  });
  const [visibleRegionIds, setVisibleRegionIds] = useState<string[] | null | undefined>();

  useEffect(() => {
    if (query.data) setVisibleRegionIds(query.data.visibleRegionIds);
  }, [query.data]);

  return {
    events: query.data?.privateEvents ?? null,
    visibleRegionIds,
    error: query.error,
    reload: query.refetch,
  };
}
